/**
 * What the content pack's rules say about this visit, grouped by what the provider should do,
 * each with its reason (guideline, page, rule and version) one tap away.
 */
import type { CdsOutput, CdsResult, Prediction } from '@assist/core';
import { t } from '../i18n.js';

type Group = 'urgent' | 'refer' | 'treatment' | 'tests' | 'advice';

const groupOf = (o: CdsOutput): Group | null => {
  if (
    o.action === 'classify' ||
    o.action === 'next_visit' ||
    o.action === 'predict' ||
    o.action === 'close'
  )
    return null;
  if (o.action === 'block') return 'urgent';
  if (o.severity === 'danger') return 'urgent';
  if (o.action === 'refer') return 'refer';
  if (o.action === 'suggest' || o.action === 'enrol') return 'treatment';
  if (o.action === 'order_test') return 'tests';
  return 'advice';
};

const GROUPS: Array<[Group, string]> = [
  ['urgent', t.groupUrgent],
  ['refer', t.groupRefer],
  ['treatment', t.groupTreatment],
  ['tests', t.groupTests],
  ['advice', t.groupAdvice],
];

/** The final classification per variable (a later rule in a stage overrides an earlier one). */
export function findings(result: CdsResult): CdsOutput[] {
  const last = new Map<string, CdsOutput>();
  for (const o of result.outputs)
    if (o.action === 'classify' && o.params.var) last.set(o.params.var, o);
  return [...last.values()].filter((o) => o.params.value !== 'false');
}

export function CdsPanel(props: {
  result: CdsResult | null;
  rulesHidden: boolean;
  /** The pack has no decision rules at all (a form written freely). */
  noRules?: boolean;
  canApply: (o: CdsOutput) => boolean;
  onApply: (o: CdsOutput) => void;
  applied: Set<string>;
  canEnrol: (programme: string) => boolean;
  /** Whether a 'refer' item will be recorded as a referral when the visit is saved. */
  referring: (o: CdsOutput) => boolean;
  onToggleReferral: (o: CdsOutput) => void;
  /** Whether an order_test item will be sent to the laboratory when the visit is saved. */
  ordering: (o: CdsOutput) => boolean;
  onToggleOrder: (o: CdsOutput) => void;
  onEnrol: (programme: string) => void;
  programmeName: (code: string) => string;
}) {
  const r = props.result;
  if (props.rulesHidden || props.noRules)
    return (
      <aside id="cds" class="cds-panel" aria-label={t.decisionSupport}>
        <h2>{t.decisionSupport}</h2>
        <p class="muted">{props.noRules ? t.noRulesInPack : t.rulesAwaitApproval}</p>
      </aside>
    );
  const shown = r?.outputs.filter((o) => groupOf(o)) ?? [];
  const anyDraft = r?.outputs.some((o) => o.draft) ?? false;
  const chips = r ? findings(r) : [];
  return (
    <aside id="cds" class="cds-panel" aria-label={t.decisionSupport} aria-live="polite">
      <h2>{t.decisionSupport}</h2>
      {anyDraft ? <p class="draft-banner">{t.draftBanner}</p> : null}
      {(r?.outputs ?? [])
        .filter((o) => o.action === 'predict' && o.prediction)
        .map((o) => (
          <RiskCard key={o.ruleId} p={o.prediction as Prediction} />
        ))}
      {chips.length ? (
        <div class="chips" aria-label={t.groupFindings}>
          {chips.map((o) => (
            <span
              key={o.ruleId}
              class={`chip sev-${o.severity}`}
              title={`${o.ruleId}@${o.ruleVersion}`}
            >
              {o.title}
            </span>
          ))}
        </div>
      ) : null}
      {!shown.length ? <p class="muted">{t.nothingYet}</p> : null}
      {GROUPS.map(([g, label]) => {
        const items = shown.filter((o) => groupOf(o) === g);
        if (!items.length) return null;
        return (
          <section key={g} class={`cds-group g-${g}`}>
            <h3>{label}</h3>
            {items.map((o) => (
              <article key={o.ruleId} class={`cds sev-${o.severity}`} data-rule={o.ruleId}>
                <div class="cds-head">
                  <strong>{o.title}</strong>
                  {o.draft ? <span class="tag draft">{t.draft}</span> : null}
                </div>
                <p>{o.message}</p>
                {o.action === 'suggest' && props.canApply(o) ? (
                  props.applied.has(o.ruleId) ? (
                    <p class="ok-note">{t.applied}</p>
                  ) : (
                    <button type="button" class="small" onClick={() => props.onApply(o)}>
                      {t.apply}
                    </button>
                  )
                ) : null}
                {o.action === 'enrol' &&
                o.params.programme &&
                props.canEnrol(o.params.programme) ? (
                  <button
                    type="button"
                    class="small primary"
                    onClick={() => props.onEnrol(o.params.programme ?? '')}
                  >
                    {t.enrolIn(props.programmeName(o.params.programme))}
                  </button>
                ) : null}
                {o.action === 'order_test' && o.params.test ? (
                  <label class="check">
                    <input
                      type="checkbox"
                      checked={props.ordering(o)}
                      onChange={() => props.onToggleOrder(o)}
                    />{' '}
                    {t.requestTest}
                  </label>
                ) : null}
                {o.action === 'refer' ? (
                  <label class="check">
                    <input
                      type="checkbox"
                      checked={props.referring(o)}
                      onChange={() => props.onToggleReferral(o)}
                    />{' '}
                    {t.recordReferral}
                  </label>
                ) : null}
                <details>
                  <summary>{t.why}</summary>
                  <ul class="why">
                    {o.sources.map((s) => (
                      <li key={s.source + s.locator}>
                        {t.source}: {s.title}, {s.locator}
                      </li>
                    ))}
                    <li>
                      {t.rule}: {o.ruleId} v{o.ruleVersion}
                      {o.draft ? ` (${t.draft})` : ''}
                    </li>
                  </ul>
                </details>
              </article>
            ))}
          </section>
        );
      })}
    </aside>
  );
}

/** An AI risk estimate: band, probability, how much was measured, and what raised it. */
export function RiskCard({ p }: { p: Prediction }) {
  const pct = p.probability === null ? null : Math.round(p.probability * 100);
  const total = p.measured.length + p.missing.length;
  return (
    <section class={`risk-card band-${p.band ?? 'none'}`} data-model={p.modelId}>
      <div class="cds-head">
        <h3>{p.title}</h3>
        {p.draft ? <span class="tag draft">{t.draft}</span> : null}
      </div>
      {p.state === 'withheld' ? (
        <p>{t.riskWithheld}</p>
      ) : (
        <>
          <div class="risk-row">
            <span class={`risk-band band-${p.band}`}>{t.bandLabel[p.band ?? ''] ?? p.band}</span>
            <span class="risk-pct">{pct}%</span>
          </div>
          {p.escalatedBy.length ? (
            <p class="risk-esc">
              {t.raisedBySafety}: {p.escalatedBy.join(', ')}
            </p>
          ) : null}
          {p.drivers.length ? (
            <p>
              {t.raisedBy}:{' '}
              {p.drivers.map((d) => `${d.label} ${Math.round(d.value * 100) / 100}`).join(' · ')}
            </p>
          ) : null}
        </>
      )}
      <p class="help">
        {t.measuredOf(p.measured.length, total)} {t.riskAdvisory}
      </p>
    </section>
  );
}
